import React, { useState, useCallback, useEffect, useRef } from "react";
import { StyleSheet } from "react-native";
import { web } from "./Platform";
import CustomTextInput from "./CustomTextInput";

// Debounce utility function
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};
//run this in web401
//C:\WEBROOT\DM\wwwRoot\google-places-proxy>node server.js
// Determine base URL based on environment
const isDevelopment = process.env.NODE_ENV === "development";
const API_BASE_URL = isDevelopment
  ? "http://localhost:8010/proxy/maps/api/place"
  : "https://www.directmortgage.com/places";
//: "https://maps.googleapis.com/maps/api/place";

const GooglePlacesAutoComplete = ({
  value = "",
  onChange,
  onAddressSelect,
  placeholder = "Enter address",
  className = "inputBox",
  containerClassName = "",
  disabled = false,
  required = false,
  name = "address",
  label = "Address",
  validate = false,
  forceValidation = false,
}) => {
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [selectedAddressIndex, setSelectedAddressIndex] = useState(-1);
  const [inputValue, setInputValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const lastSelectedValue = useRef("");
  const lastInputValue = useRef("");
  const isSelecting = useRef(false);
  const hasUserModified = useRef(false);
  const inputRef = useRef(null);
  const lastPropValue = useRef(value);

  // Update input value when prop changes
  useEffect(() => {
    let newValue = "";
    let propValue = value;
    if (propValue == "[object Object]") propValue = "";
    if (typeof propValue === "string") {
      newValue = propValue;
    } else if (
      propValue &&
      typeof propValue === "object" &&
      propValue.fullAddress
    ) {
      newValue = propValue.fullAddress;
    } else {
      console.warn("Invalid value prop (LoanData.subjectAddress):", propValue);
    }
    if (
      newValue !== inputValue &&
      newValue !== lastPropValue.current &&
      !isSelecting.current &&
      !hasUserModified.current &&
      (newValue || !lastSelectedValue.current)
    ) {
      setInputValue(newValue);
      lastSelectedValue.current = newValue;
      lastPropValue.current = newValue;
      setShowSuggestions(false);
      console.log(
        "useEffect: LoanData.subjectAddress=",
        propValue,
        "newValue=",
        newValue,
        "inputValue=",
        inputValue
      );
    }
  }, [value, inputValue]);

  // Google Places Autocomplete API search function
  const searchAddress = async (query) => {
    if (!isFocused || query.length < 3 || isSelecting.current) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      console.log(
        "searchAddress skipped: isFocused=",
        isFocused,
        "query.length=",
        query.length,
        "isSelecting=",
        isSelecting.current
      );
      return;
    }

    setIsLoadingAddress(true);

    try {
      const autocompleteResponse = await fetch(
        //`${API_BASE_URL}/autocomplete/json?input=${encodeURIComponent(
        `${API_BASE_URL}/places-autocomplete?input=${encodeURIComponent(
          query
        )}&types=address&components=country:us&key=${
          process.env.REACT_APP_GOOGLE_API_KEY
        }`,
        {
          //   mode: "cors",
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (!autocompleteResponse.ok) {
        throw new Error(`HTTP error! Status: ${autocompleteResponse.status}`);
      }

      const autocompleteData = await autocompleteResponse.json();
      if (autocompleteData.status !== "OK") {
        throw new Error(`Google Places API error: ${autocompleteData.status}`);
      }

      const formattedSuggestions = await Promise.all(
        autocompleteData.predictions.map(async (item) => {
          try {
            const detailsResponse = await fetch(
              //`${API_BASE_URL}/details/json?place_id=${item.place_id}&fields=address_components,geometry&key=${process.env.REACT_APP_GOOGLE_API_KEY}`,
              `${API_BASE_URL}/place-details?place_id=${item.place_id}&fields=address_components,geometry&key=${process.env.REACT_APP_GOOGLE_API_KEY}`,
              {
                // mode: "cors",
                headers: {
                  Accept: "application/json",
                },
              }
            );
            if (!detailsResponse.ok) {
              throw new Error(`Details API error: ${detailsResponse.status}`);
            }
            const detailsData = await detailsResponse.json();
            if (detailsData.status !== "OK") {
              return null;
            }

            const addressComponents = detailsData.result.address_components;
            const getComponent = (type) =>
              addressComponents.find((comp) => comp.types.includes(type))
                ?.long_name || "";

            const getStateComponent = (type) =>
              addressComponents.find((comp) => comp.types.includes(type))
                ?.short_name || "";

            const suggestion = {
              display_name: item.description,
              lat: detailsData.result.geometry.location.lat,
              lon: detailsData.result.geometry.location.lng,
              address: {
                house_number: getComponent("street_number"),
                road: getComponent("route"),
                city:
                  getComponent("locality") ||
                  getComponent("sublocality") ||
                  getComponent("administrative_area_level_3") ||
                  "",
                state: getStateComponent("administrative_area_level_1"),
                postcode: getComponent("postal_code") || "",
              },
            };
            console.log("Formatted suggestion:", suggestion);
            return suggestion;
          } catch (error) {
            console.error(
              `Error fetching details for place_id ${item.place_id}:`,
              error
            );
            return null;
          }
        })
      );

      const validSuggestions = formattedSuggestions.filter(
        (item) => item !== null
      );
      console.log("Valid suggestions:", validSuggestions);
      setAddressSuggestions(validSuggestions);
      setShowSuggestions(validSuggestions.length > 0 && isFocused);
    } catch (error) {
      console.error("Address search error:", error);
      setAddressSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoadingAddress(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((query) => {
      console.log("debouncedSearch called with:", query);
      searchAddress(query);
    }, 500),
    [isFocused]
  );

  const handleAddressInputChange = (text) => {
    setInputValue(text);
    hasUserModified.current = true;
    isSelecting.current = false;
    if (onChange) {
      const syntheticEvent = {
        target: { name, value: text },
      };
      console.log(
        "handleAddressInputChange: Calling onChange with:",
        syntheticEvent
      );
      onChange(syntheticEvent);
    }
    if (text !== lastInputValue.current) {
      debouncedSearch(text);
    }
    setSelectedAddressIndex(-1);
    console.log(
      "handleAddressInputChange: text=",
      text,
      "isSelecting=",
      isSelecting.current,
      "inputValue=",
      inputValue
    );
  };

  const handleAddressSelect = (suggestion) => {
    if (!suggestion || !suggestion.address) {
      console.error("Invalid suggestion in handleAddressSelect:", suggestion);
      return;
    }
    isSelecting.current = true;
    hasUserModified.current = false;
    console.log("handleAddressSelect: suggestion=", suggestion);

    const formattedAddress =
      `${suggestion.address.house_number || ""} ${
        suggestion.address.road || ""
      }`.trim() || suggestion.display_name;

    const addressData = {
      fullAddress: formattedAddress,
      streetAddress: formattedAddress,
      city: suggestion.address.city,
      state: suggestion.address.state,
      zipCode: suggestion.address.postcode,
      lat: suggestion.lat,
      lon: suggestion.lon,
      displayName: suggestion.display_name,
    };

    console.log(
      "handleAddressSelect: formattedAddress=",
      formattedAddress,
      "addressData=",
      addressData
    );

    setInputValue(formattedAddress);
    lastSelectedValue.current = formattedAddress;
    lastInputValue.current = formattedAddress;
    lastPropValue.current = formattedAddress;

    // Delay to ensure state updates propagate
    setTimeout(() => {
      if (onChange) {
        const syntheticEvent = {
          target: { name, value: formattedAddress },
        };
        console.log(
          "handleAddressSelect: Calling onChange with:",
          syntheticEvent
        );
        onChange(syntheticEvent);
      }

      if (onAddressSelect) {
        console.log(
          "handleAddressSelect: Calling onAddressSelect with:",
          addressData
        );
        setTimeout(() => {
          onAddressSelect(addressData);
        }, 0);
      }
    }, 0);

    setAddressSuggestions([]);
    setShowSuggestions(false);
    setSelectedAddressIndex(-1);
    isSelecting.current = false;
  };

  const handleAddressKeyDown = (e) => {
    console.log("handleAddressKeyDown: key=", e.key);
    if (!showSuggestions || addressSuggestions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedAddressIndex((prev) =>
          prev < addressSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedAddressIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        e.stopPropagation();
        if (selectedAddressIndex >= 0) {
          hasUserModified.current = false;
          console.log(
            "Enter pressed: selectedAddressIndex=",
            selectedAddressIndex,
            "suggestion=",
            addressSuggestions[selectedAddressIndex]
          );
          handleAddressSelect(addressSuggestions[selectedAddressIndex]);
        }
        break;

      case "Escape":
        e.preventDefault();
        setShowSuggestions(false);
        setAddressSuggestions([]);
        setSelectedAddressIndex(-1);
        if (inputRef.current) {
          inputRef.current.blur();
        }
        break;
      case "Tab":
        setShowSuggestions(false);
        setAddressSuggestions([]);
        if (inputValue && lastSelectedValue.current) {
          hasUserModified.current = false;
          handleAddressSelect({
            display_name: inputValue,
            address: {
              house_number: "",
              road: inputValue,
              city: "",
              state: "",
              postcode: "",
            },
            lat: "",
            lon: "",
          });
        }
        break;
    }
  };

  const handleBlur = () => {
    if (isSelecting.current) {
      console.log("handleBlur: Skipping due to isSelecting=true");
      return;
    }
    setIsFocused(false);
    setShowSuggestions(false);
    setAddressSuggestions([]);
    if (lastSelectedValue.current && !inputValue) {
      const restoredValue =
        typeof lastSelectedValue.current === "string"
          ? lastSelectedValue.current
          : "";
      setInputValue(restoredValue);
      if (onChange) {
        const syntheticEvent = {
          target: { name, value: restoredValue },
        };
        console.log("handleBlur: Calling onChange with:", syntheticEvent);
        onChange(syntheticEvent);
      }
      console.log(
        "handleBlur: Restored inputValue=",
        restoredValue,
        "lastSelectedValue=",
        lastSelectedValue.current
      );
    }
    hasUserModified.current = false;
    lastInputValue.current = inputValue;
  };

  return (
    <div
      className={`inputBoxContainer ${containerClassName}`}
      style={styles.inputBoxContainer}
    >
      {label && (
        <span className="inputBoxLabel" style={styles.inputBoxLabel}>
          {label}
        </span>
      )}
      <CustomTextInput
        ref={inputRef}
        value={typeof inputValue === "string" ? inputValue : ""}
        onChangeText={(text) => {
          console.log("CustomTextInput: onChangeText called with:", text);
          if (!isSelecting.current) {
            handleAddressInputChange(text);
          }
        }}
        onKeyPress={(e) => {
          console.log("CustomTextInput: onKeyPress called with key=", e.key);
          handleAddressKeyDown(e);
        }}
        onBlur={handleBlur}
        onFocus={() => {
          setIsFocused(true);
          lastInputValue.current = inputValue;
          console.log(
            "onFocus: inputValue=",
            inputValue,
            "lastInputValue=",
            lastInputValue.current,
            "isFocused=",
            true
          );
        }}
        placeholder={placeholder}
        className={className}
        style={styles.inputBoxOverride}
        disabled={disabled}
        required={required}
        name={name}
        validate={validate}
        forceValidation={forceValidation}
        autoCapitalize="none"
        autoComplete="off"
        placeholderTextColor="#999"
      />

      {isLoadingAddress && (
        <div style={styles.loadingContainer}>
          <span style={styles.attributionText}>Loading...</span>
        </div>
      )}

      {showSuggestions && addressSuggestions.length > 0 && (
        <div style={styles.suggestionsContainer}>
          {addressSuggestions.map((suggestion, index) => (
            <div
              key={index}
              onClick={() => {
                console.log(
                  "Suggestion clicked: index=",
                  index,
                  "suggestion=",
                  suggestion
                );
                handleAddressSelect(suggestion);
              }}
              style={{
                ...styles.suggestionItem,
                ...(selectedAddressIndex === index
                  ? styles.selectedSuggestion
                  : {}),
                ...(index === addressSuggestions.length - 1
                  ? styles.lastSuggestion
                  : {}),
              }}
              onMouseEnter={() => setSelectedAddressIndex(index)}
            >
              <span style={styles.suggestionText}>
                {suggestion.display_name}
              </span>
            </div>
          ))}
          <div style={styles.attributionContainer}>
            <span style={styles.attributionText}>Powered by Google Places</span>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = StyleSheet.create({
  inputBoxContainer: {
    flexDirection: "row",
    borderWidth: 1,
    borderRadius: 8,
    padding: 8,
    marginBottom: 20,
    position: "relative",
    backgroundColor: "#fff",
    width: "95%",
    border: "1px solid rgb(192, 192, 192)",
  },
  inputBoxLabel: {
    position: "absolute",
    backgroundColor: "#fff",
    top: -12,
    left: 3,
    fontSize: 14,
    color: "gray",
    paddingHorizontal: 3,
    zIndex: 1,
    fontWeight: "bold",
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  },
  inputBoxOverride: {
    width: "100%",
    borderWidth: 0,
    height: "34px",
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    borderRadius: "5px",
    color: "rgb(81, 87, 93)",
    padding: "5px",
  },
  inputBoxFocus: {
    borderColor: "#66afe9",
    boxShadow: "inset 0 1px 1px rgba(0,0,0,.075), 0 0 8px rgba(102,175,233,.6)",
    WebkitBoxShadow:
      "inset 0 1px 1px rgba(0,0,0,.075), 0 0 8px rgba(102,175,233,.6)",
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    borderRadius: "5px",
    color: "rgb(81, 87, 93)",
    padding: "5px",
  },
  loadingContainer: {
    position: "absolute",
    right: 10,
    top: "50%",
    transform: [{ translateY: -10 }],
  },
  suggestionsContainer: {
    position: "sticky",
    top: "100%",
    marginTop: -1,
    left: 0,
    right: 0,
    backgroundColor: "#ffffff",
    borderWidth: 1,
    borderColor: "#cccccc",
    borderTopWidth: 0,
    borderBottomLeftRadius: 6,
    borderBottomRightRadius: 6,
    maxHeight: 250,
    zIndex: 20000,
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    overflowY: "auto",
  },
  suggestionItem: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
  },
  lastSuggestion: {
    borderBottomWidth: 0,
  },
  selectedSuggestion: {
    backgroundColor: "#f0f0f0",
  },
  suggestionText: {
    fontSize: 15,
    color: "#333333",
    lineHeight: 2,
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  },
  attributionContainer: {
    padding: 6,
    paddingHorizontal: 8,
    borderTopWidth: 1,
    borderTopColor: "#eee",
    backgroundColor: "#f9f9f9",
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
  },
  attributionText: {
    fontSize: 10,
    color: "#666",
  },
});

export default GooglePlacesAutoComplete;
// AIzaSyCqld7Iv74VCXQmkR7gmPUZWMsm4VfNs-U
//AIzaSyCePMRtJMjq0bBZ64ESWSIvFHR6cvwUiYk
